import {
  classifyReadableWebDocument,
  classifyWebHost,
  unreadableWebInspection,
  type WebHostClassification,
  type WebServiceInspection,
} from "./webFingerprint.ts";

export type {
  WebDeviceKind,
  WebHostClassification,
  WebInspectionConfidence,
  WebServiceInspection,
} from "./webFingerprint.ts";

export type WebProbeProtocol = "http" | "https";

export type WebProbePort = {
  port: number;
  protocol: WebProbeProtocol;
  label: string;
};

export type WebProbeTarget = WebProbePort & {
  host: string;
};

export type WebProbeService = WebProbePort & {
  latencyMs: number;
  inspection: WebServiceInspection;
};

export type WebScanHost = {
  ip: string;
  services: WebProbeService[];
  classification: WebHostClassification;
};

export type WebScanProgress = {
  phase: "probing" | "inspecting";
  completed: number;
  total: number;
  percent: number;
  currentHost: string;
  responsiveServices: number;
};

export type WebScanResult = {
  subnet: string;
  attemptedHosts: number;
  attemptedServices: number;
  responsiveServices: number;
  readableServices: number;
  classifiedHosts: number;
  responsiveHosts: WebScanHost[];
  startedAt: string;
  completedAt: string;
};

export type LocalNetworkPermissionState =
  | PermissionState
  | "unsupported";

export const WEB_SCAN_PORTS: readonly WebProbePort[] = [
  { port: 80, protocol: "http", label: "HTTP" },
  { port: 443, protocol: "https", label: "HTTPS" },
  { port: 8080, protocol: "http", label: "HTTP alternatif" },
  { port: 8443, protocol: "https", label: "HTTPS alternatif" },
];

const PERMISSION_PROBE_TARGETS: readonly Pick<WebProbeTarget, "protocol" | "port">[] = [
  { protocol: "http", port: 80 },
  { protocol: "https", port: 443 },
  { protocol: "http", port: 8080 },
];

type FetchWithLocalAddressSpace = (
  input: RequestInfo | URL,
  init?: RequestInit & { targetAddressSpace?: "local" },
) => Promise<Response>;

type ScanOptions = {
  subnet: string;
  concurrency?: number;
  timeoutMs?: number;
  signal?: AbortSignal;
  onProgress?: (progress: WebScanProgress) => void;
  fetchImpl?: FetchWithLocalAddressSpace;
  ports?: readonly WebProbePort[];
  hostStart?: number;
  hostEnd?: number;
  inspectResponses?: boolean;
  inspectionConcurrency?: number;
  inspectionTimeoutMs?: number;
  maximumInspections?: number;
};

type LocalNetworkAccessOptions = {
  subnet: string;
  signal?: AbortSignal;
  fetchImpl?: FetchWithLocalAddressSpace;
  permissionStateImpl?: () => Promise<LocalNetworkPermissionState>;
  pollIntervalMs?: number;
  promptTimeoutMs?: number;
};

type ProbeOutcome = WebProbeTarget & {
  reachable: boolean;
  latencyMs: number;
};

export class WebScanError extends Error {
  public readonly code:
    | "invalid-subnet"
    | "invalid-range"
    | "unsupported"
    | "permission-denied"
    | "permission-required"
    | "permission-timeout"
    | "aborted";

  constructor(
    code:
      | "invalid-subnet"
      | "invalid-range"
      | "unsupported"
      | "permission-denied"
      | "permission-required"
      | "permission-timeout"
      | "aborted",
    message: string,
  ) {
    super(message);
    this.code = code;
    this.name = "WebScanError";
  }
}

export function normalizePrivateSubnet(value: string): string | null {
  const normalized = value
    .trim()
    .replace(/\.$/, "")
    .replace(/\.0\/24$/, "");
  const octets = normalized.split(".");

  if (octets.length !== 3) return null;
  const numbers = octets.map((octet) => {
    if (!/^\d{1,3}$/.test(octet)) return Number.NaN;
    return Number(octet);
  });

  if (numbers.some((number) => !Number.isInteger(number) || number < 0 || number > 255)) {
    return null;
  }

  const [first, second] = numbers;
  const isPrivate =
    first === 10 ||
    (first === 172 && second >= 16 && second <= 31) ||
    (first === 192 && second === 168);

  return isPrivate ? numbers.join(".") : null;
}

export function isLoopbackHostname(value: string): boolean {
  const hostname = value
    .trim()
    .toLowerCase()
    .replace(/^\[/, "")
    .replace(/\]$/, "")
    .replace(/\.$/, "");

  if (hostname === "localhost" || hostname.endsWith(".localhost") || hostname === "::1") {
    return true;
  }

  const octets = hostname.split(".");
  return (
    octets.length === 4 &&
    octets[0] === "127" &&
    octets.every((octet) => /^\d{1,3}$/.test(octet) && Number(octet) <= 255)
  );
}

export function buildProbeTargets(
  subnet: string,
  ports: readonly WebProbePort[] = WEB_SCAN_PORTS,
  hostStart = 1,
  hostEnd = 254,
): WebProbeTarget[] {
  const normalizedSubnet = normalizePrivateSubnet(subnet);
  if (!normalizedSubnet) {
    throw new WebScanError(
      "invalid-subnet",
      "Le préfixe doit désigner un réseau IPv4 privé, par exemple 192.168.1.",
    );
  }
  if (
    !Number.isInteger(hostStart) ||
    !Number.isInteger(hostEnd) ||
    hostStart < 1 ||
    hostEnd > 254 ||
    hostStart > hostEnd
  ) {
    throw new WebScanError("invalid-range", "La plage d’adresses demandée est invalide.");
  }

  const targets: WebProbeTarget[] = [];
  for (let host = hostStart; host <= hostEnd; host += 1) {
    for (const port of ports) {
      targets.push({ host: `${normalizedSubnet}.${host}`, ...port });
    }
  }
  return targets;
}

export async function getLocalNetworkPermissionState(): Promise<LocalNetworkPermissionState> {
  if (typeof navigator === "undefined" || !navigator.permissions?.query) {
    return "unsupported";
  }

  for (const name of ["local-network", "local-network-access"] as const) {
    try {
      const result = await navigator.permissions.query(
        { name } as unknown as PermissionDescriptor,
      );
      return result.state;
    } catch {
      // Try the backwards-compatible permission name next.
    }
  }
  return "unsupported";
}

function createAbortError(): WebScanError {
  return new WebScanError("aborted", "Le scan a été interrompu.");
}

function waitForPermissionPoll(delayMs: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(createAbortError());

  return new Promise((resolve, reject) => {
    const timeout = globalThis.setTimeout(() => {
      signal?.removeEventListener("abort", abort);
      resolve();
    }, delayMs);
    const abort = () => {
      globalThis.clearTimeout(timeout);
      reject(createAbortError());
    };
    signal?.addEventListener("abort", abort, { once: true });
  });
}

/**
 * Triggers Chrome's Local Network Access prompt through a small set of common
 * gateway endpoints, waits for the decision, then cancels the requests before
 * the bounded scan starts.
 */
export async function requestLocalNetworkAccess(
  options: LocalNetworkAccessOptions,
): Promise<void> {
  const subnet = normalizePrivateSubnet(options.subnet);
  if (!subnet) {
    throw new WebScanError(
      "invalid-subnet",
      "Le préfixe doit désigner un réseau IPv4 privé, par exemple 192.168.1.",
    );
  }
  if (options.signal?.aborted) throw createAbortError();

  const fetchImpl = options.fetchImpl ?? globalThis.fetch?.bind(globalThis);
  const readPermission = options.permissionStateImpl ?? getLocalNetworkPermissionState;
  if (!fetchImpl) {
    throw new WebScanError(
      "unsupported",
      "Ce navigateur ne permet pas les requêtes réseau nécessaires.",
    );
  }

  const initialState = await readPermission();
  if (initialState === "granted") return;
  if (initialState === "denied") {
    throw new WebScanError(
      "permission-denied",
      "L’accès au réseau local a été refusé dans les paramètres du navigateur.",
    );
  }
  if (initialState === "unsupported") {
    throw new WebScanError(
      "unsupported",
      "Ce scan expérimental nécessite une version récente de Chrome avec l’accès au réseau local.",
    );
  }

  const controller = new AbortController();
  const abortFromParent = () => controller.abort(options.signal?.reason);
  options.signal?.addEventListener("abort", abortFromParent, { once: true });
  const startedAt = Date.now();
  const promptTimeoutMs = Math.max(5_000, Math.min(options.promptTimeoutMs ?? 90_000, 180_000));
  const pollIntervalMs = Math.max(25, Math.min(options.pollIntervalMs ?? 250, 1_000));
  let requestSettledAt: number | null = null;

  const promptRequests = [1, 254].flatMap((host) =>
    PERMISSION_PROBE_TARGETS.map(({ protocol, port }) =>
      fetchImpl(
        `${protocol}://${subnet}.${host}:${port}/`,
        {
          method: "GET",
          mode: "no-cors",
          cache: "no-store",
          credentials: "omit",
          redirect: "follow",
          referrerPolicy: "no-referrer",
          signal: controller.signal,
          targetAddressSpace: "local",
        },
      ).catch(() => undefined),
    ),
  );
  const promptRequest = Promise.all(promptRequests).finally(() => {
      requestSettledAt = Date.now();
  });

  try {
    while (true) {
      if (options.signal?.aborted) throw createAbortError();
      const state = await readPermission();
      if (state === "granted") return;
      if (state === "denied") {
        throw new WebScanError(
          "permission-denied",
          "L’accès au réseau local a été refusé dans les paramètres du navigateur.",
        );
      }
      if (state === "unsupported") {
        throw new WebScanError(
          "unsupported",
          "Le navigateur n’expose pas l’autorisation réseau local nécessaire.",
        );
      }
      if (Date.now() - startedAt >= promptTimeoutMs) {
        throw new WebScanError(
          "permission-timeout",
          "La demande d’accès au réseau local a expiré sans réponse.",
        );
      }
      if (requestSettledAt !== null && Date.now() - requestSettledAt >= 750) {
        throw new WebScanError(
          "permission-required",
          "L’autorisation réseau local n’a pas été accordée. Vérifiez les paramètres Chrome du site.",
        );
      }
      await waitForPermissionPoll(pollIntervalMs, options.signal);
    }
  } finally {
    controller.abort();
    options.signal?.removeEventListener("abort", abortFromParent);
    await promptRequest;
  }
}

async function probeTarget(
  target: WebProbeTarget,
  timeoutMs: number,
  fetchImpl: FetchWithLocalAddressSpace,
  parentSignal?: AbortSignal,
): Promise<ProbeOutcome> {
  if (parentSignal?.aborted) throw createAbortError();

  const controller = new AbortController();
  const abortFromParent = () => controller.abort(parentSignal?.reason);
  parentSignal?.addEventListener("abort", abortFromParent, { once: true });
  const timeout = globalThis.setTimeout(() => controller.abort(), timeoutMs);
  const startedAt = Date.now();
  const probeUrl = `${target.protocol}://${target.host}:${target.port}/`;

  try {
    await fetchImpl(probeUrl, {
      method: "GET",
      mode: "no-cors",
      cache: "no-store",
      credentials: "omit",
      redirect: "follow",
      referrerPolicy: "no-referrer",
      signal: controller.signal,
      targetAddressSpace: "local",
    });
    return { ...target, reachable: true, latencyMs: Date.now() - startedAt };
  } catch {
    if (parentSignal?.aborted) throw createAbortError();
    return { ...target, reachable: false, latencyMs: Date.now() - startedAt };
  } finally {
    // A no-cors response is opaque. Stop downloading its body once Fetch has
    // established that the request resolved; no status, header or body is read.
    controller.abort();
    globalThis.clearTimeout(timeout);
    parentSignal?.removeEventListener("abort", abortFromParent);
  }
}

function groupResponsiveHosts(outcomes: ProbeOutcome[]): WebScanHost[] {
  const responsiveHosts = new Map<string, WebProbeService[]>();
  for (const outcome of outcomes) {
    if (!outcome.reachable) continue;
    const services = responsiveHosts.get(outcome.host) ?? [];
    services.push({
      port: outcome.port,
      protocol: outcome.protocol,
      label: outcome.label,
      latencyMs: outcome.latencyMs,
      inspection: unreadableWebInspection(),
    });
    responsiveHosts.set(outcome.host, services);
  }

  return [...responsiveHosts.entries()]
    .map(([ip, services]) => {
      const sortedServices = services.sort((left, right) => left.port - right.port);
      return {
        ip,
        services: sortedServices,
        classification: classifyWebHost(
          ip,
          sortedServices.map((service) => service.inspection),
        ),
      };
    })
    .sort((left, right) => {
      const leftHost = Number(left.ip.split(".").at(-1));
      const rightHost = Number(right.ip.split(".").at(-1));
      return leftHost - rightHost;
    });
}

const INSPECTION_BODY_LIMIT = 32 * 1024;

function isTextualContentType(contentType: string | undefined): boolean {
  if (!contentType) return true;
  const normalized = contentType.toLowerCase();
  return (
    normalized.startsWith("text/") ||
    normalized.includes("html") ||
    normalized.includes("json") ||
    normalized.includes("xml") ||
    normalized.includes("javascript") ||
    normalized.includes("x-www-form-urlencoded")
  );
}

async function readTextPrefix(response: Response, maximumBytes = INSPECTION_BODY_LIMIT): Promise<string> {
  if (!response.body) return "";

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;

  try {
    while (received < maximumBytes) {
      const { done, value } = await reader.read();
      if (done) break;
      const remaining = maximumBytes - received;
      const chunk = value.byteLength > remaining ? value.subarray(0, remaining) : value;
      chunks.push(chunk);
      received += chunk.byteLength;
      if (value.byteLength > remaining) break;
    }
  } finally {
    await reader.cancel().catch(() => undefined);
  }

  const bytes = new Uint8Array(received);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return new TextDecoder().decode(bytes);
}

function collectReadableHeaders(response: Response): Record<string, string> {
  const readableHeaders: Record<string, string> = {};
  for (const name of [
    "content-language",
    "content-length",
    "content-type",
    "last-modified",
    "server",
    "www-authenticate",
    "x-powered-by",
  ]) {
    const value = response.headers.get(name)?.trim();
    if (value) readableHeaders[name] = value.slice(0, 160);
  }
  return readableHeaders;
}

async function inspectService(
  target: WebProbeTarget,
  timeoutMs: number,
  fetchImpl: FetchWithLocalAddressSpace,
  parentSignal?: AbortSignal,
): Promise<WebServiceInspection> {
  if (parentSignal?.aborted) throw createAbortError();

  const controller = new AbortController();
  const abortFromParent = () => controller.abort(parentSignal?.reason);
  parentSignal?.addEventListener("abort", abortFromParent, { once: true });
  const timeout = globalThis.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetchImpl(
      `${target.protocol}://${target.host}:${target.port}/`,
      {
        method: "GET",
        mode: "cors",
        cache: "no-store",
        credentials: "omit",
        redirect: "follow",
        referrerPolicy: "no-referrer",
        signal: controller.signal,
        targetAddressSpace: "local",
      },
    );

    // Do not inspect a page that redirected away from the selected local host.
    if (response.url) {
      const finalUrl = new URL(response.url);
      if (finalUrl.hostname !== target.host) return unreadableWebInspection();
    }

    const contentType = response.headers.get("content-type")?.slice(0, 160) || undefined;
    if (!isTextualContentType(contentType)) return unreadableWebInspection();

    const body = await readTextPrefix(response);
    return classifyReadableWebDocument({
      body,
      contentType,
      headers: collectReadableHeaders(response),
    });
  } catch {
    if (parentSignal?.aborted) throw createAbortError();
    // Usually CORS, TLS or another browser policy. The first opaque probe still
    // remains a valid reachability observation.
    return unreadableWebInspection();
  } finally {
    controller.abort();
    globalThis.clearTimeout(timeout);
    parentSignal?.removeEventListener("abort", abortFromParent);
  }
}

async function inspectResponsiveHosts(
  hosts: WebScanHost[],
  options: {
    concurrency: number;
    timeoutMs: number;
    maximumInspections: number;
    signal?: AbortSignal;
    fetchImpl: FetchWithLocalAddressSpace;
    onProgress?: (progress: WebScanProgress) => void;
    responsiveServices: number;
  },
): Promise<void> {
  const services = hosts
    .flatMap((host) => host.services.map((service) => ({ host, service })))
    .slice(0, options.maximumInspections);
  if (services.length === 0) return;

  let cursor = 0;
  let completed = 0;
  options.onProgress?.({
    phase: "inspecting",
    completed,
    total: services.length,
    percent: 90,
    currentHost: "",
    responsiveServices: options.responsiveServices,
  });

  const runWorker = async () => {
    while (true) {
      if (options.signal?.aborted) throw createAbortError();
      const currentIndex = cursor;
      cursor += 1;
      if (currentIndex >= services.length) return;

      const { host, service } = services[currentIndex];
      service.inspection = await inspectService(
        { host: host.ip, port: service.port, protocol: service.protocol, label: service.label },
        options.timeoutMs,
        options.fetchImpl,
        options.signal,
      );
      completed += 1;
      options.onProgress?.({
        phase: "inspecting",
        completed,
        total: services.length,
        percent: 90 + Math.round((completed / services.length) * 10),
        currentHost: host.ip,
        responsiveServices: options.responsiveServices,
      });
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(options.concurrency, services.length) }, () => runWorker()),
  );

  for (const host of hosts) {
    host.classification = classifyWebHost(
      host.ip,
      host.services.map((service) => service.inspection),
    );
  }
}

export async function scanWebNetwork(options: ScanOptions): Promise<WebScanResult> {
  const startedAt = new Date();
  const ports = options.ports ?? WEB_SCAN_PORTS;
  const hostStart = options.hostStart ?? 1;
  const hostEnd = options.hostEnd ?? 254;
  const targets = buildProbeTargets(options.subnet, ports, hostStart, hostEnd);
  const concurrency = Math.max(1, Math.min(options.concurrency ?? 12, 24));
  const timeoutMs = Math.max(250, Math.min(options.timeoutMs ?? 1_500, 4_000));
  const inspectResponses = options.inspectResponses ?? true;
  const inspectionConcurrency = Math.max(1, Math.min(options.inspectionConcurrency ?? 4, 8));
  const inspectionTimeoutMs = Math.max(
    500,
    Math.min(options.inspectionTimeoutMs ?? 2_000, 5_000),
  );
  const maximumInspections = Math.max(1, Math.min(options.maximumInspections ?? 32, 64));
  const fetchImpl = options.fetchImpl ?? globalThis.fetch?.bind(globalThis);

  if (!fetchImpl) {
    throw new WebScanError("unsupported", "Ce navigateur ne permet pas les requêtes réseau nécessaires.");
  }
  if (options.signal?.aborted) throw createAbortError();

  let cursor = 0;
  let completed = 0;
  let responsiveServices = 0;
  let lastReportedPercent = -1;
  const outcomes: ProbeOutcome[] = [];

  const runWorker = async () => {
    while (true) {
      if (options.signal?.aborted) throw createAbortError();
      const currentIndex = cursor;
      cursor += 1;
      if (currentIndex >= targets.length) return;

      const target = targets[currentIndex];
      const outcome = await probeTarget(target, timeoutMs, fetchImpl, options.signal);
      outcomes.push(outcome);
      completed += 1;
      if (outcome.reachable) responsiveServices += 1;

      const percent = Math.round(
        (completed / targets.length) * (inspectResponses ? 90 : 100),
      );
      if (percent !== lastReportedPercent || outcome.reachable) {
        lastReportedPercent = percent;
        options.onProgress?.({
          phase: "probing",
          completed,
          total: targets.length,
          percent,
          currentHost: target.host,
          responsiveServices,
        });
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, targets.length) }, () => runWorker()),
  );

  const normalizedSubnet = normalizePrivateSubnet(options.subnet);
  if (!normalizedSubnet) {
    throw new WebScanError("invalid-subnet", "Le préfixe réseau est invalide.");
  }

  const responsiveHosts = groupResponsiveHosts(outcomes);
  if (inspectResponses && responsiveServices > 0) {
    await inspectResponsiveHosts(responsiveHosts, {
      concurrency: inspectionConcurrency,
      timeoutMs: inspectionTimeoutMs,
      maximumInspections,
      signal: options.signal,
      fetchImpl,
      onProgress: options.onProgress,
      responsiveServices,
    });
  } else if (inspectResponses) {
    options.onProgress?.({
      phase: "probing",
      completed: targets.length,
      total: targets.length,
      percent: 100,
      currentHost: "",
      responsiveServices: 0,
    });
  }

  const readableServices = responsiveHosts.reduce(
    (total, host) => total + host.services.filter((service) => service.inspection.readable).length,
    0,
  );
  const classifiedHosts = responsiveHosts.filter(
    (host) => host.classification.kind !== "unknown",
  ).length;

  return {
    subnet: normalizedSubnet,
    attemptedHosts: hostEnd - hostStart + 1,
    attemptedServices: targets.length,
    responsiveServices,
    readableServices,
    classifiedHosts,
    responsiveHosts,
    startedAt: startedAt.toISOString(),
    completedAt: new Date().toISOString(),
  };
}
